import {
  PaymentDetailsFeesList,
  PaymentDetailsProductsList,
  PaymentDetailsSubtotal,
  PaymentDetailsServicesList,
  PaymentDetailsBundlesList,
  PaymentDetailsGiftCardList,
  PaymentDetailsMethod,
} from '@/components/elements/PaymentDetails';

import { ListItem, ListItemContent } from '@/components/elements/lists';

import BookingListItem from '@/components/elements/lists/BookingListItem/BookingListItem';
import DataGrid from '@/components/elements/lists/DataGrid';
import Alert from '@/components/elements/notifications/Alert/Alert';
import Icon from '@/components/icons/Icon';

import { CheckoutDetailsProps } from '@/components/modules/checkout/CheckoutDetails/CheckoutDetails';
import { CHECKOUT_PAYMENT_METHOD } from '@/constants/checkout';
import { TermLinkIdentifier, TERMS_NAVIGATION_LINKS } from '@/constants/terms';
import { getCanCancelAppointmentProps, isBookingInThePast, setBookingStartingPoint } from '@/helpers';
import {
  mapConfirmedBookingInfoGiftcards,
  mapConfirmedBookingInfoProducts,
  mapConfirmedBookingInfoServices,
  mapConfirmedBookingToCheckoutDetailsProps,
} from '@/helpers/confirmation';
import { _s } from '@/locale';
import { ConfirmedBooking } from '@/types/api/services/booking';

const baseTranslationKey = 'components.modules.appointment.AppointmentDetailsSummary';

const AppointmentDetailsSummary = ({ appointment }: { appointment: ConfirmedBooking }) => {
  const { cancelInformation, canCancel } = getCanCancelAppointmentProps(appointment);
  const isUpcoming = !isBookingInThePast(appointment);

  const checkoutDetailsProps: CheckoutDetailsProps = {
    ...mapConfirmedBookingToCheckoutDetailsProps({ booking: appointment }),
    services: mapConfirmedBookingInfoServices(appointment, true),
    giftcards: mapConfirmedBookingInfoGiftcards(appointment),
    products: mapConfirmedBookingInfoProducts(appointment),
  };

  const { fees, services, giftcards, subtotal, products, bundles, paymentMethod, information, priceToPay } =
    checkoutDetailsProps;

  const showCancelInformation = isUpcoming && cancelInformation ? cancelInformation : undefined;
  const status = appointment.status + (!isUpcoming ? 100 : 0);

  const termsVariant = ((): TermLinkIdentifier => {
    if (Boolean(checkoutDetailsProps.giftcards.length > 0)) {
      return TermLinkIdentifier.COUPON;
    }

    switch (checkoutDetailsProps.paymentMethod?.type) {
      case CHECKOUT_PAYMENT_METHOD.APPLE_PAY:
      case CHECKOUT_PAYMENT_METHOD.GOOGLE_PAY:
      case CHECKOUT_PAYMENT_METHOD.STORED_COF:
      case CHECKOUT_PAYMENT_METHOD.NEW_COF:
      case CHECKOUT_PAYMENT_METHOD.QLIRO:
        return TermLinkIdentifier.PAYMENT;
    }

    return TermLinkIdentifier.USAGE;
  })();

  const termDescription = _s(`${baseTranslationKey}.terms.description.${termsVariant}`);
  /**
   * We want the link to the terms to be to the general usage terms instread of the giftcard terms for this page
   */
  const termIdentifier = termsVariant === TermLinkIdentifier.COUPON ? TermLinkIdentifier.USAGE : termsVariant;
  const { label: termLabel, to: termLink } = TERMS_NAVIGATION_LINKS.find((term) => term.identifier === termIdentifier);

  const { place } = appointment;
  const placePageUrl = place?.about?.active ? `/places/${place?.about?.slug}-${place?.id}` : null;

  return (
    <>
      <BookingListItem
        image={{ src: checkoutDetailsProps.logo }}
        {...checkoutDetailsProps}
        {...(showCancelInformation && { underline: canCancel })}
        status={status}
        services={''}
        companyAvatarPosition={'right'}
        src={placePageUrl}
        onClick={() => setBookingStartingPoint('my_bookings_view_place_details')}
      />
      {showCancelInformation && canCancel && (
        <ListItem type="div" underline leftSlot={<Icon variant="info-circle" />}>
          <ListItemContent content={cancelInformation} />
        </ListItem>
      )}
      {showCancelInformation && !canCancel && (
        <div className="px-lg pb-md">
          <Alert
            variant="cation"
            leftSlot={<Icon variant="info-circle" color="warning-900" />}
            body={cancelInformation}
          />
        </div>
      )}
      <div className="px-lg">
        <DataGrid ariaLabel={_s(`${baseTranslationKey}.ariaLabel.title`)}>
          <PaymentDetailsServicesList services={services} baseTranslationKey={baseTranslationKey} />
          <PaymentDetailsProductsList products={products} baseTranslationKey={baseTranslationKey} />
          <PaymentDetailsBundlesList bundles={bundles} baseTranslationKey={baseTranslationKey} />
          <PaymentDetailsGiftCardList giftcards={giftcards} baseTranslationKey={baseTranslationKey} />
          <PaymentDetailsSubtotal subtotal={subtotal} baseTranslationKey={baseTranslationKey} />
          <PaymentDetailsFeesList fees={fees} baseTranslationKey={baseTranslationKey} />
          <DataGrid.Row hasPaddingTop={!subtotal && !fees?.length}>
            <DataGrid.Cell title={priceToPay.label} type="header" />
            <DataGrid.Cell
              title={priceToPay.price}
              type="header"
              align="right"
              ariaLabel={_s(`${baseTranslationKey}.ariaLabel.subtotal.amount`)}
            />
          </DataGrid.Row>
        </DataGrid>
        <PaymentDetailsMethod
          paymentMethod={paymentMethod}
          information={information}
          baseTranslationKey={baseTranslationKey}
        />
      </div>
      <Alert
        variant="information"
        body={
          <>
            {termDescription}{' '}
            <a className="underline" href={termLink} target="_blank" rel="noopener noreferrer">
              {termLabel}
            </a>
          </>
        }
      />
    </>
  );
};

export default AppointmentDetailsSummary;
